import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Group,
  Circle,
  Text,
} from 'react-konva';
import useImage from 'use-image';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getEffectiveBookingStatus } from '../../utils/bookingActionability';

// Status → marker color. Vacant = light gray. Rejected / Expired free the slot
// so we don't render them; they fall back to "vacant" when no other booking
// holds the placement.
const STATUS_COLOR = {
  pending: '#f59e0b',
  approved: '#fb923c',
  paid: '#22c55e',
};
const VACANT_COLOR = '#d1d5db';
const SELECTED_COLOR = '#FFBD59';

const STATUS_LABEL_KEY = {
  pending: 'dashboard.notif_pending',
  approved: 'dashboard.notif_approved',
  paid: 'dashboard.notif_paid',
};

const isOccupyingStatus = (s) =>
  s === 'pending' || s === 'approved' || s === 'paid';

const PromoterFloorplanView = ({ event, bookings = [] }) => {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [selectedId, setSelectedId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const floorplanUrl = event?.floorplanUrl || null;
  const placements = useMemo(
    () => (Array.isArray(event?.placements) ? event.placements : []),
    [event?.placements]
  );
  const spaces = useMemo(
    () => event?.spaces || event?.Spaces || [],
    [event?.spaces, event?.Spaces]
  );

  // No crossOrigin: the floorplan bucket doesn't return CORS headers, so
  // requesting `anonymous` would fail to load the image entirely. We only
  // display it; the download path uses an HTML print window (no canvas read)
  // so a tainted canvas is fine here.
  const [image] = useImage(floorplanUrl);
  const naturalW = image?.naturalWidth || 800;
  const naturalH = image?.naturalHeight || 600;

  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const scale = Math.min(1, containerWidth / naturalW);
  const stageW = naturalW * scale;
  const stageH = naturalH * scale;

  // Active bookings keyed by placementId so we know who occupies each slot.
  // Active = effective status in (pending, approved, paid). Rejected / Expired
  // are dropped so the placement reads as vacant.
  const occupants = useMemo(() => {
    const map = new Map();
    for (const b of bookings || []) {
      if (!b?.placementId) continue;
      const s = getEffectiveBookingStatus(b);
      if (!isOccupyingStatus(s)) continue;
      // If two bookings collide on the same placementId (data anomaly), prefer
      // the most "advanced" one: paid > approved > pending.
      const prev = map.get(b.placementId);
      const rank = { pending: 1, approved: 2, paid: 3 };
      if (!prev || rank[s] > rank[getEffectiveBookingStatus(prev)]) {
        map.set(b.placementId, b);
      }
    }
    return map;
  }, [bookings]);

  // Ordered list of occupied placements → numbered captions ("1: Brand · Space")
  const captions = useMemo(() => {
    const occupied = placements.filter((p) => occupants.has(p?.id));
    return occupied.map((p, idx) => {
      const booking = occupants.get(p.id);
      const space = spaces.find(
        (s) => s.id === p.spaceId || s.id === p.spaceTypeId || s.name === p.spaceName
      );
      const brandName =
        booking?.Brand?.username ||
        booking?.Brand?.name ||
        booking?.brandName ||
        t('common.unknown_brand', 'Unknown brand');
      const spaceName = space?.name || p.spaceName || '—';
      return {
        number: idx + 1,
        placementId: p.id,
        brandName,
        spaceName,
        status: getEffectiveBookingStatus(booking),
      };
    });
  }, [placements, occupants, spaces, t]);

  const captionByPlacement = useMemo(() => {
    const map = new Map();
    captions.forEach((c) => map.set(c.placementId, c));
    return map;
  }, [captions]);

  const selectedPlacement = useMemo(
    () => placements.find((p) => p.id === selectedId) || null,
    [placements, selectedId]
  );
  const selectedBooking = selectedId ? occupants.get(selectedId) : null;
  const selectedSpace = selectedPlacement
    ? spaces.find(
        (s) =>
          s.id === selectedPlacement.spaceId ||
          s.id === selectedPlacement.spaceTypeId ||
          s.name === selectedPlacement.spaceName
      )
    : null;

  const colorForPlacement = (placementId, isSelected) => {
    if (isSelected) return SELECTED_COLOR;
    const booking = occupants.get(placementId);
    if (!booking) return VACANT_COLOR;
    const s = getEffectiveBookingStatus(booking);
    return STATUS_COLOR[s] || VACANT_COLOR;
  };

  // Open a new window with the floorplan + overlaid markers + legend, then
  // trigger the browser print dialog. The user can choose "Save as PDF" to
  // download. This avoids canvas.toDataURL() entirely so it works even when
  // the floorplan image is served without CORS headers.
  const handleDownload = () => {
    if (!floorplanUrl) return;
    setDownloading(true);
    try {
      const win = window.open('', '_blank', 'width=1024,height=900');
      if (!win) {
        setDownloading(false);
        return;
      }

      const escape = (s) =>
        String(s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      const markersHtml = placements
        .filter((p) => p?.id)
        .map((p) => {
          const cap = captionByPlacement.get(p.id);
          const isOccupied = occupants.has(p.id);
          const status = isOccupied
            ? getEffectiveBookingStatus(occupants.get(p.id))
            : null;
          const color = status
            ? STATUS_COLOR[status] || VACANT_COLOR
            : VACANT_COLOR;
          const label = cap ? cap.number : '';
          return `<div class="marker" style="left:${(p.xPercent ?? 0) * 100}%;top:${(p.yPercent ?? 0) * 100}%;background:${color};">${label}</div>`;
        })
        .join('');

      const legendHtml = captions
        .map(
          (c) =>
            `<li><span class="num" style="background:${STATUS_COLOR[c.status] || VACANT_COLOR}">${c.number}</span><span class="brand">${escape(c.brandName)}</span> — <span class="space">${escape(c.spaceName)}</span></li>`
        )
        .join('');

      const title = `${escape(event?.name || 'Event')} — ${t('dashboard.floorplan_tab', 'Floorplan')}`;

      win.document.open();
      win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, Segoe UI, sans-serif; margin: 24px; color: #111827; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .meta { color: #6b7280; font-size: 12px; margin-bottom: 16px; }
  .map { position: relative; display: inline-block; max-width: 100%; }
  .map img { display: block; max-width: 100%; height: auto; }
  .marker {
    position: absolute; transform: translate(-50%, -50%);
    width: 26px; height: 26px; border-radius: 50%;
    border: 2px solid #1f2937; color: #fff; font-weight: 700; font-size: 12px;
    display: flex; align-items: center; justify-content: center;
  }
  .legend-title { font-size: 14px; font-weight: 700; margin: 24px 0 8px; }
  .legend { list-style: none; padding: 0; margin: 0; columns: 2; column-gap: 32px; }
  .legend li { display: flex; align-items: baseline; gap: 8px; padding: 4px 0; break-inside: avoid; font-size: 13px; }
  .legend .num {
    display: inline-flex; align-items: center; justify-content: center;
    width: 20px; height: 20px; border-radius: 50%; color: #fff;
    font-size: 11px; font-weight: 700; flex-shrink: 0;
  }
  .legend .brand { font-weight: 600; }
  .legend .space { color: #6b7280; }
  @media print {
    body { margin: 12mm; }
  }
</style>
</head>
<body>
  <h1>${escape(event?.name || 'Event')}</h1>
  <div class="meta">${t('dashboard.floorplan_tab', 'Floorplan')}</div>
  <div class="map">
    <img src="${escape(floorplanUrl)}" alt="" />
    ${markersHtml}
  </div>
  ${
    captions.length > 0
      ? `<div class="legend-title">${t('dashboard.floorplan_brand_legend', 'Brand placements')}</div>
         <ol class="legend">${legendHtml}</ol>`
      : ''
  }
  <script>
    (function () {
      var img = document.querySelector('.map img');
      function go() {
        try { window.focus(); window.print(); } catch (e) {}
      }
      if (img && !img.complete) {
        img.addEventListener('load', go);
        img.addEventListener('error', go);
      } else {
        setTimeout(go, 100);
      }
    })();
  </script>
</body>
</html>`);
      win.document.close();
    } catch (err) {
      console.error('Failed to open print view:', err);
    } finally {
      setDownloading(false);
    }
  };

  if (!floorplanUrl) {
    return (
      <div className="bg-white rounded-2xl shadow p-8 text-center text-sm text-gray-500">
        {t(
          'dashboard.floorplan_not_available',
          'This event does not have a floorplan.'
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-6">
      {/* Top bar: legend + download */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
          <LegendDot color={STATUS_COLOR.pending} label={t('dashboard.notif_pending', 'Pending')} />
          <LegendDot color={STATUS_COLOR.approved} label={t('dashboard.notif_approved', 'Approved')} />
          <LegendDot color={STATUS_COLOR.paid} label={t('dashboard.notif_paid', 'Paid')} />
          <LegendDot color={VACANT_COLOR} label={t('dashboard.floorplan_vacant', 'Available')} />
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || !image}
          className="inline-flex items-center gap-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 px-3 py-2 text-sm font-semibold text-white shadow transition"
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          {t('dashboard.floorplan_download', 'Download map')}
        </button>
      </div>

      {/* Floorplan stage */}
      <div
        ref={containerRef}
        className="relative w-full bg-white rounded-2xl shadow overflow-hidden"
      >
        {image ? (
          <Stage
            ref={stageRef}
            width={stageW}
            height={stageH}
            onClick={(e) => {
              if (e.target === stageRef.current) setSelectedId(null);
            }}
          >
            <Layer>
              <KonvaImage
                image={image}
                width={stageW}
                height={stageH}
                listening={false}
              />
              {placements.map((p) => {
                if (!p?.id) return null;
                const x = (p.xPercent ?? 0) * stageW;
                const y = (p.yPercent ?? 0) * stageH;
                const isSelected = selectedId === p.id;
                const fill = colorForPlacement(p.id, isSelected);
                const cap = captionByPlacement.get(p.id);
                const label = cap ? String(cap.number) : '';
                return (
                  <Group
                    key={p.id}
                    onClick={() =>
                      setSelectedId((cur) => (cur === p.id ? null : p.id))
                    }
                    onTap={() =>
                      setSelectedId((cur) => (cur === p.id ? null : p.id))
                    }
                  >
                    <Circle
                      x={x}
                      y={y}
                      radius={13}
                      fill={fill}
                      stroke={isSelected ? '#1f2937' : '#374151'}
                      strokeWidth={isSelected ? 3 : 2}
                    />
                    {label && (
                      <Text
                        x={x}
                        y={y}
                        text={label}
                        fontSize={11}
                        fontStyle="bold"
                        fill="#ffffff"
                        align="center"
                        verticalAlign="middle"
                        offsetX={label.length > 1 ? 8 : 4}
                        offsetY={5}
                        listening={false}
                      />
                    )}
                  </Group>
                );
              })}
            </Layer>
          </Stage>
        ) : (
          <div className="w-full h-72 flex items-center justify-center text-sm text-gray-400">
            {t('dashboard.floorplan_loading', 'Loading floorplan…')}
          </div>
        )}

        {/* InfoWindow on click */}
        {selectedPlacement && image && (
          <InfoWindow
            stageW={stageW}
            stageH={stageH}
            placement={selectedPlacement}
            booking={selectedBooking}
            space={selectedSpace}
            caption={captionByPlacement.get(selectedPlacement.id)}
            onClose={() => setSelectedId(null)}
            t={t}
          />
        )}
      </div>

    </div>
  );
};

const LegendDot = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      className="inline-block w-3 h-3 rounded-full"
      style={{ backgroundColor: color }}
    />
    {label}
  </span>
);

const InfoWindow = ({ stageW, stageH, placement, booking, space, caption, onClose, t }) => {
  const TOOLTIP_W = 260;
  const TOOLTIP_H = 170;
  const PAD = 12;
  const GAP = 16;

  const x = (placement.xPercent ?? 0) * stageW;
  const y = (placement.yPercent ?? 0) * stageH;

  const placeAbove = y - TOOLTIP_H - GAP >= 0;
  const rawTop = placeAbove ? y - GAP : y + GAP;
  const translateY = placeAbove ? '-100%' : '0%';
  const minLeft = PAD + TOOLTIP_W / 2;
  const maxLeft = stageW - PAD - TOOLTIP_W / 2;
  const clampedLeft = Math.max(minLeft, Math.min(x, maxLeft));

  const status = booking ? getEffectiveBookingStatus(booking) : null;
  const statusColor = status ? STATUS_COLOR[status] || VACANT_COLOR : VACANT_COLOR;
  const statusLabel = status
    ? t(STATUS_LABEL_KEY[status] || '', { defaultValue: status })
    : t('dashboard.floorplan_vacant', 'Available');

  return (
    <div
      className="absolute w-[260px] bg-white/95 backdrop-blur-md border border-gray-200 shadow-xl rounded-2xl z-20 px-4 py-3"
      style={{
        top: rawTop,
        left: clampedLeft,
        transform: `translate(-50%, ${translateY})`,
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {caption && (
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white shrink-0"
              style={{ backgroundColor: statusColor }}
            >
              {caption.number}
            </span>
          )}
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {space?.name || placement.spaceName || t('common.space', 'Space')}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xs"
          aria-label="close"
        >
          ✕
        </button>
      </div>

      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold text-white mb-2"
        style={{ backgroundColor: statusColor }}
      >
        {statusLabel}
      </span>

      {booking ? (
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>{t('common.brand', 'Brand')}</span>
            <span className="font-medium text-gray-900 truncate ml-2">
              {booking?.Brand?.username || booking?.Brand?.name || booking?.brandName || '—'}
            </span>
          </div>
          {space?.size != null && (
            <div className="flex justify-between text-gray-600">
              <span>{t('event_create.size', 'Size')}</span>
              <span className="font-medium text-gray-900">
                {space.size} m<sup>2</sup>
              </span>
            </div>
          )}
          {space?.price != null && (
            <div className="flex justify-between text-gray-600">
              <span>{t('common.price', 'Price')}</span>
              <span className="font-medium text-gray-900">€{space.price}</span>
            </div>
          )}
          {booking?.totalAmount != null && (
            <div className="flex justify-between text-gray-600">
              <span>{t('common.total', 'Total')}</span>
              <span className="font-medium text-gray-900">€{booking.totalAmount}</span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-500 italic">
          {t(
            'dashboard.floorplan_vacant_hint',
            'No booking is currently using this spot.'
          )}
        </p>
      )}
    </div>
  );
};

export default PromoterFloorplanView;
